/*
 * Reusable Spanish translations for history-tracking entities and fields.
 * Keyed by the registered mongoose model name (must match backend `mongoose.models` keys).
 * Used by the tracking-configuration screen now, and by history-display modals in a later phase.
 */
export const entityTranslations = {
	debt: {
		label: 'Deuda',
		fields: {
			hasPreviousDebts: 'Tiene deuda anterior',
			previousDebt: 'Deuda anterior',
			previousDebtDate: 'Fecha de deuda anterior',
			capital: 'Capital',
			amount: 'Monto cuota',
			interest: '% Interés',
			type: 'Tipo',
			concept: 'Concepto',
			conceptRef: 'Concepto (referencia)',
			dues: 'Cuotas',
			date: 'Fecha',
			periodicity: 'Periodicidad',
			periodicityNumMonths: 'Meses de periodicidad',
			owner: 'Deudor',
			contract: 'Contrato',
			assetSale: 'Venta de activo',
			currency: 'Moneda',
			customId: 'Identificador',
			endDate: 'Fecha vto',
			closeDate: 'Fecha finalización',
			balance: 'Saldo',
			guaranteedAmount: 'Monto garantizado',
			firstDueExpiration: 'Vencimiento primera cuota',
			dueDay: 'Día de vencimiento',
			numMonths: 'Cantidad de meses',
			selectedMonthOption: 'Opción de mes',
			createWithPreviousDues: 'Crear con cuotas anteriores',
			createWithPreviousPayments: 'Crear con pagos anteriores',
			createdBy: 'Creado por',
			updateDateOption: 'Opción de actualización de fecha',
			updatedDateAt: 'Fecha de actualización',
			updatedDateBy: 'Actualizado por',
			behavior: 'Comportamiento',
			billingType: 'Tipo de facturación',
			pricePerUser: 'Precio por usuario',
			lastConfirmedUserCount: 'Última cantidad confirmada de usuarios',
			generatedDebts: 'Cuotas generadas',
		},
	},
	credit: {
		label: 'Pasividad',
		fields: {
			hasPreviousCredits: 'Tiene pasividad anterior',
			previousCredit: 'Pasividad anterior',
			previousCreditDate: 'Fecha de pasividad anterior',
			capital: 'Capital',
			amount: 'Monto cuota',
			interest: '% Interés',
			concept: 'Concepto',
			conceptRef: 'Concepto (referencia)',
			behavior: 'Comportamiento',
			dues: 'Cuotas',
			date: 'Fecha',
			periodicity: 'Periodicidad',
			periodicityNumMonths: 'Meses de periodicidad',
			owner: 'Acreedor',
			contract: 'Contrato',
			currency: 'Moneda',
			customId: 'Identificador',
			endDate: 'Fecha vto',
			balance: 'Saldo',
			guaranteedAmount: 'Total garantizado',
			firstDueExpiration: 'Vencimiento primera cuota',
			dueDay: 'Día de vencimiento',
			numMonths: 'Cantidad de meses',
			selectedMonthOption: 'Opción de mes',
			createWithPreviousDues: 'Crear con cuotas anteriores',
			createWithPreviousPayments: 'Crear con pagos anteriores',
			createdBy: 'Creado por',
			updateDateOption: 'Opción de actualización de fecha',
			updatedDateAt: 'Fecha de actualización',
			updatedDateBy: 'Actualizado por',
			generatedCredits: 'Cuotas generadas',
		},
	},
	payment: {
		label: 'Cobro',
		fields: {
			amount: 'Monto',
			date: 'Fecha',
			debt: 'Deuda',
			debtDue: 'Cuota',
			observations: 'Observaciones',
			folder: 'Carpeta',
			currency: 'Moneda',
			status: 'Estado',
			comprobante: 'Comprobante',
			customId: 'Identificador',
			paidBy: 'Pagado por',
			paymentMethod: 'Método de pago',
		},
	},
	creditPayment: {
		label: 'Pago',
		fields: {
			amount: 'Monto',
			date: 'Fecha',
			credit: 'Pasividad',
			creditDue: 'Cuota',
			observations: 'Observaciones',
			folder: 'Carpeta',
			currency: 'Moneda',
			comprobante: 'Comprobante',
			customId: 'Identificador',
		},
	},
	bill: {
		label: 'Gasto',
		fields: {
			name: 'Nombre',
			amount: 'Monto',
			expirationDate: 'Vencimiento',
			startDate: 'Fecha de inicio',
			asset: 'Activo fijo',
			type: 'Tipo',
			periodicity: 'Periodicidad',
			period: 'Período',
			observations: 'Observaciones',
			folder: 'Carpeta',
			responsible: 'Responsable',
			account: 'Cuenta',
			billTemplate: 'Plantilla',
			customId: 'Identificador',
			comprobante: 'Comprobante',
			confirmed: 'Confirmado',
			confirmationDate: 'Fecha de confirmación',
			paid: 'Pago?',
			partyType: 'Tipo de parte',
			bankAccount: 'Cuenta bancaria',
		},
	},
	contract: {
		label: 'Alquiler',
		fields: {
			hasPreviousDebts: 'Tiene saldo anterior',
			previousDebt: 'Saldo anterior',
			previousDebtDate: 'Fecha de saldo anterior',
			periodInit: 'Inicio',
			periodEnd: 'Fin',
			amount: 'Monto',
			owner: 'Inquilino',
			field: 'Detalles',
			rental: 'Alquiler',
			property: 'Propiedad',
			ubication: 'Ubicación',
			folder: 'Carpeta',
			observations: 'Observaciones',
			paymentMethod: 'Método de pago',
			customId: 'Identificador',
			currency: 'Moneda',
			status: 'Estado',
			closeDate: 'Fecha finalización',
			closeReason: 'Motivo de finalización',
			lastAdjustmentDate: 'Fecha último ajuste',
			housingUnit: 'Unidad habitacional',
			lastAdjustmentIpc: 'Porcentaje último ajuste',
			paymentTo: 'Pago a',
			bankAccount: 'Cuenta bancaria',
			responsible: 'Responsable',
			debts: 'Deudas',
			credits: 'Pasividades',
		},
	},
	account: {
		label: 'Cuenta',
		fields: {
			name: 'Nombre',
			email: 'Email',
			phone: 'Teléfono',
			address: 'Dirección',
			type: 'Tipo',
			mainContact: 'Contacto principal',
			createdBy: 'Creado por',
			observations: 'Observaciones',
			folder: 'Carpeta',
			rut: 'RUT',
			tipoDocumento: 'Tipo de documento',
			nroDocumento: 'Número de documento',
			currency: 'Moneda',
			bankAccountUSD: 'Cuenta bancaria (USD)',
			bankAccountUYU: 'Cuenta bancaria (UYU)',
		},
	},
	contact: {
		label: 'Contacto',
		fields: {
			name: 'Nombre',
			email: 'Email',
			address: 'Dirección',
			phone: 'Teléfono',
			account: 'Cuenta',
			observations: 'Observaciones',
			folder: 'Carpeta',
			owner: 'Propietario',
		},
	},
	asset: {
		label: 'Activo Fijo',
		fields: {
			name: 'Nombre',
			price: 'Precio/Valor',
			type: 'Tipo',
			ubication: 'Ubicación',
			description: 'Descripción',
			housingUnit: 'Unidad habitacional',
			owner: 'Propietario',
			observations: 'Observaciones',
			folder: 'Carpeta',
			escritura: 'Escritura',
			currency: 'Moneda',
			guaranteedAmount: 'Monto garantizado',
			customId: 'Identificador',
			multipleLots: 'Múltiples padrones?',
			lot: 'Padrón',
			sold: 'Vendido',
			responsible: 'Responsable',
		},
	},
	AssetSale: {
		label: 'Venta de Activo',
		fields: {
			date: 'Fecha',
			amount: 'Monto',
			currency: 'Moneda',
			sumDebts: 'Total deudas',
			asset: 'Activo fijo',
			purchaser: 'Comprador',
			document: 'Documento',
			customId: 'Identificador',
			initialPayment: 'Pago inicial',
			lot: 'Padrón',
			housingUnit: 'Unidad habitacional',
			paymentTo: 'Pago a',
			bankAccount: 'Cuenta bancaria',
			responsible: 'Responsable',
		},
	},
	GeneratedDebt: {
		label: 'Cuota de Deuda',
		fields: {
			amount: 'Monto',
			debt: 'Deuda',
			date: 'Fecha',
			customId: 'Identificador',
			paid: 'Pago?',
			interest: 'Interés',
			amortization: 'Amortización',
			balance: 'Saldo',
			expiration: 'Vencimiento',
			status: 'Estado',
			surcharge: 'Recargo',
		},
	},
	GeneratedCredit: {
		label: 'Cuota de Pasividad',
		fields: {
			amount: 'Monto',
			credit: 'Pasividad',
			date: 'Fecha',
			customId: 'Identificador',
			paid: 'Pago?',
			interest: 'Interés',
			amortization: 'Amortización',
			balance: 'Saldo',
			expiration: 'Vencimiento',
		},
	},
	user: {
		label: 'Usuario',
		fields: {
			name: 'Nombre',
			email: 'Email',
			password: 'Contraseña',
			isAdmin: 'Es administrador',
			role: 'Rol',
			isVerified: 'Verificado',
			resetPasswordToken: 'Token de restablecimiento',
			resetPasswordExpire: 'Vencimiento del restablecimiento',
			verifyToken: 'Token de verificación',
			refreshToken: 'Sesiones',
			contact: 'Contacto',
			phone: 'Teléfono',
			signature: 'Firma',
			googleCalendar: 'Google Calendar',
		},
	},
	bankAccount: {
		label: 'Cuenta Bancaria',
		fields: {
			owner: 'Titular',
			bank: 'Banco',
			branch: 'Sucursal',
			accountType: 'Tipo de cuenta',
			accountNumber: 'Número de cuenta',
			currency: 'Moneda',
		},
	},
	interest: {
		label: 'Interés',
		fields: {
			percentage: 'Porcentaje',
			type: 'Tipo',
			debt: 'Deuda',
			observations: 'Observaciones',
			folder: 'Carpeta',
		},
	},
	warranty: {
		label: 'Garantía',
		fields: {
			option: 'Opción',
			debt: 'Deuda',
			asset: 'Activo fijo',
			amount: 'Monto',
			currency: 'Moneda',
		},
	},
	commission: {
		label: 'Comisión',
		fields: {
			seller: 'Vendedor',
			isExternalSeller: 'Vendedor externo',
			externalSellerName: 'Nombre de vendedor externo',
			amount: 'Monto',
			currency: 'Moneda',
			percentage: 'Porcentaje',
			calculationBase: 'Base de cálculo',
			contract: 'Contrato',
			assetSale: 'Venta de activo',
			observations: 'Observaciones',
		},
	},
	DebtConcept: {
		label: 'Concepto de Deuda',
		fields: {
			name: 'Nombre',
			active: 'Activo',
			order: 'Orden',
			key: 'Clave',
			behavior: 'Comportamiento',
			requiresContract: 'Requiere contrato',
			requiresAsset: 'Requiere activo',
			requiresAssetSale: 'Requiere venta',
			treatAsExpense: 'Es gasto',
			ipcAdjustable: 'Ajusta por IPC',
			commissionable: 'Genera comisión',
			commissionPercentage: '% Comisión',
			allowsPerUser: 'Permite por usuario',
			defaultPeriodicity: 'Periodicidad por defecto',
		},
	},
	CreditConcept: {
		label: 'Concepto de Pasividad',
		fields: {
			name: 'Nombre',
			active: 'Activo',
			order: 'Orden',
			key: 'Clave',
			behavior: 'Comportamiento',
			requiresContract: 'Requiere contrato',
			requiresAsset: 'Requiere activo',
			requiresAssetSale: 'Requiere venta',
			treatAsExpense: 'Es gasto',
			ipcAdjustable: 'Ajusta por IPC',
			commissionable: 'Genera comisión',
			commissionPercentage: '% Comisión',
			defaultPeriodicity: 'Periodicidad por defecto',
		},
	},
};

export const getEntityLabel = (model) => entityTranslations[model]?.label || model;

export const getFieldLabel = (model, field) =>
	entityTranslations[model]?.fields?.[field] || field;

// modelo -> route builder, matching the detail-page routes already defined
// in App.js. Models with no detail screen (payment, creditPayment, bill
// sub-records) have no entry, so callers render them as plain text.
const ENTITY_ROUTES = {
	account: (id) => `/AdminScreen/accounts/account/${id}`,
	debt: (id) => `/AdminScreen/debts/debt/${id}`,
	credit: (id) => `/AdminScreen/credits/credit/${id}`,
	contract: (id) => `/AdminScreen/contracts/contract/${id}`,
	asset: (id) => `/AdminScreen/assets/asset/${id}`,
	AssetSale: (id) => `/AdminScreen/assetSales/assetSale/${id}`,
	bill: (id) => `/AdminScreen/bills/bill/${id}`,
};

export const getEntityRoute = (model, id) => ENTITY_ROUTES[model]?.(id) || null;
